import type { FC, SVGProps } from "react";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  ActionsEnum,
  RebuildRouteTypeEnum,
  type RydeUiUnit,
  type TPermissionAction,
} from "@ryde/shared";

const RIDE_ENTITY: RydeUiUnit = "ride";

interface IRideBulkActionMeta {
  labelKey: string;
  hintKey: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  permission: { entity: RydeUiUnit; action: TPermissionAction };
}

/**
 * Label, hint, icon and permission of every ride bulk action, shared by the rides grid
 * drawer and the rides-report drawer so the two lists cannot drift apart. Each drawer
 * supplies only the `onClick` for the actions it offers.
 */
export const RIDE_BULK_ACTION_META = {
  archive: {
    labelKey: "general.ARCHIVE_ORDERS",
    hintKey: "ride.bulk.hints.archive",
    icon: SVG_MUI.ArchiveSVG,
    permission: { entity: RIDE_ENTITY, action: "delete" },
  },
  sendToSupplier: {
    labelKey: "general.SEND_TO_SUPPLIER",
    hintKey: "ride.bulk.hints.sendToSupplier",
    icon: SVG_MUI.SendSVG,
    permission: { entity: RIDE_ENTITY, action: "send_to_supplier" },
  },
  reject: {
    labelKey: "general.REJECT_RIDES",
    hintKey: "ride.bulk.hints.reject",
    icon: SVG_MUI.CircleXMark,
    permission: { entity: RIDE_ENTITY, action: "delete" },
  },
  cancel: {
    labelKey: "general.CANCEL_RIDES",
    hintKey: "ride.bulk.hints.cancel",
    icon: SVG_MUI.CancelSVG,
    permission: { entity: RIDE_ENTITY, action: "cancel" },
  },
  recalculatePrice: {
    labelKey: "general.RECALCULATE_PRICE",
    hintKey: "ride.bulk.hints.recalculatePrice",
    icon: SVG_MUI.Coins2SVG,
    permission: { entity: RIDE_ENTITY, action: "recalculate_prices" },
  },
  recalculateSupplierPrice: {
    labelKey: "general.RECALCULATE_SUPPLIER_PRICE",
    hintKey: "ride.bulk.hints.recalculateSupplierPrice",
    icon: SVG_MUI.Briefcase2SVG,
    permission: {
      entity: "system" as RydeUiUnit,
      action: ActionsEnum.UpdateSupplierPrices,
    },
  },
  rebuildRoutes: {
    labelKey: "general.REBUILD_ROUTES",
    hintKey: "ride.bulk.hints.rebuildRoutes",
    icon: SVG_MUI.ArrowsRotate3SVG,
    permission: { entity: RIDE_ENTITY, action: "rebuild" },
  },
  rebuildRoutesCustom: {
    labelKey: "general.REBUILD_ROUTES_CUSTOM",
    hintKey: "ride.bulk.hints.rebuildRoutesCustom",
    icon: SVG_MUI.ArrowsRotateDot3SVG,
    permission: { entity: RIDE_ENTITY, action: "rebuild" },
  },
  rebuildRide: {
    labelKey: "general.REBUILD_RIDE",
    hintKey: "ride.bulk.hints.rebuildRide",
    icon: SVG_MUI.RotateRight2SVG,
    permission: { entity: RIDE_ENTITY, action: "rebuild" },
  },
  updateStations: {
    labelKey: "general.UPDATE_STATIONS",
    hintKey: "ride.bulk.hints.updateStations",
    icon: SVG_MUI.StopAtStation4SVG,
    permission: { entity: RIDE_ENTITY, action: "rebuild" },
  },
  fitVehicleAndRecalculate: {
    labelKey: "general.FIT_VEHICLE_AND_RECALCULATE",
    hintKey: "ride.bulk.hints.fitVehicleAndRecalculate",
    icon: SVG_MUI.MenuBusSVG,
    permission: { entity: RIDE_ENTITY, action: "update" },
  },
  sendMessages: {
    labelKey: "general.SEND_MESSAGES",
    hintKey: "ride.bulk.hints.sendMessages",
    icon: SVG_MUI.MessageLines3SVG,
    permission: { entity: "passenger" as RydeUiUnit, action: "send_message" },
  },
  sendSms: {
    labelKey: "ride.SEND_SMS",
    hintKey: "ride.bulk.hints.sendSms",
    icon: PIC.MessageSvg,
    permission: { entity: RIDE_ENTITY, action: "send_to_supplier" },
  },
  fetchTrackingHistory: {
    labelKey: "general.FETCH_TRACKING_HISTORY",
    hintKey: "ride.bulk.hints.fetchTrackingHistory",
    icon: SVG_MUI.GpsActive4SVG,
    permission: { entity: RIDE_ENTITY, action: "update" },
  },
  duplicateRides: {
    labelKey: "general.DUPLICATE_RIDES",
    hintKey: "ride.bulk.hints.duplicateRides",
    icon: SVG_MUI.CalendarOutRight3SVG,
    permission: { entity: RIDE_ENTITY, action: "create" },
  },
  assignJourney: {
    labelKey: "ride.assignAJourney",
    hintKey: "ride.bulk.hints.assignJourney",
    icon: PIC.JourneySvg,
    permission: { entity: RIDE_ENTITY, action: "update" },
  },
  updateRides: {
    labelKey: "general.UPDATE_RIDES",
    hintKey: "ride.bulk.hints.updateRides",
    icon: PIC.PencilSVG,
    permission: { entity: RIDE_ENTITY, action: "update" },
  },
  printExportReports: {
    labelKey: "general.PRINT_EXPORT_REPORTS",
    hintKey: "ride.bulk.hints.printExportReports",
    icon: SVG_MUI.ReportsIconSVG,
    permission: {
      entity: "system" as RydeUiUnit,
      action: ActionsEnum.AllowExport,
    },
  },
  addReturnRide: {
    labelKey: "general.ADD_RETURN_RIDE",
    hintKey: "ride.bulk.hints.addReturnRide",
    icon: SVG_MUI.WayBack2SVG,
    permission: { entity: RIDE_ENTITY, action: "delete" },
  },
  approve: {
    labelKey: "general.APPROVE",
    hintKey: "ride.bulk.hints.approve",
    icon: SVG_MUI.CircleCheck5SVG,
    permission: { entity: RIDE_ENTITY, action: "approve" },
  },
} as const satisfies Record<string, IRideBulkActionMeta>;

/** Ride print: dropdown sources whose labels the PDF shows, and how printed values are joined. */
export const RIDE_BULK_PRINT = {
  dropdownSource: { area: "area", flight: "flight" },
  listDelimiter: ", ",
  partsDelimiter: " - ",
} as const satisfies {
  dropdownSource: Record<string, RydeUiUnit>;
  listDelimiter: string;
  partsDelimiter: string;
};

/** Rebuild routes (custom): the first option is the default OSRM-first rebuild. */
export const RIDE_BULK_REBUILD_ROUTES_CUSTOM = {
  defaultRebuildType: RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst,
} as const;

export type TRideBulkActionKey = keyof typeof RIDE_BULK_ACTION_META;
